import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { FaExpand, FaCompress } from 'react-icons/fa';
import Select from 'react-select';

// SVG Icons (unchanged)
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
const PlusIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);
const TrashIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);
const SaveIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"></path>
    <polyline points="17 21 17 13 7 13 7 21"></polyline>
    <polyline points="7 3 7 8 15 8"></polyline>
  </svg>
);
const AutofillIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const API_URL = '/api';

// Status options - UPDATED to show Active/Inactive
const STATUS_OPTIONS = [
  { value: '1', label: 'Active' },
  { value: '0', label: 'Inactive' },
];
// Payment terms options
const PAYMENT_TERMS_OPTIONS = [
  { value: '', label: 'Select Payment Terms' },
  { value: 'Cash', label: 'Cash' },
  { value: 'Credit', label: 'Credit' },
  { value: 'Card', label: 'Card' },
  { value: 'Other', label: 'Other' },
];

const initialHeader = {
  soNumber: '',
  soDate: new Date().toISOString().split('T')[0],
  customerCode: '',
  currencyCode: '',
  deliveryDate: '',
  paymentTerms: '',
  remarks: '',
  locationCode: '', // Added locationCode to header
  billingAddressID: '', // Added billing address ID
  shippingAddressID: '', // Added shipping address ID
  customerContactID: '', // Added customer contact ID
  customerOrderNumber: '', // NEW
  customerOrderDate: '',   // NEW
  customerDueDate: '',     // NEW
  IsActive: '1',
  SubTotal: '0',
  TotalDiscount: '0',
  TotalTax: '0',
  TotalAmount: '0',
  branchCode: '', // Add this line
  department_id: '', // Add department_id field
};

const initialItem = {
  productCode: '',
  productName: '',
  description: '',
  uomID: '',
  quantity: '',
  unitPrice: '',
  discount: '0',
  taxCode: '',
  total: '0',
  taxAmount: '0',
  remainingQuantity: '',
  dispatchQuantity: '',
  department_id: '', // Add department_id field
};

// ERP Command Constants
const ERP_COMMANDS = {
  SAVE_POPUP_ITEMS: 'SAVE_POPUP_ITEMS',
  LOAD_SAVED_ITEMS: 'LOAD_SAVED_ITEMS',
  DELETE_SAVED_ITEMS: 'DELETE_SAVED_ITEMS',
  AUTOFILL_ITEMS: 'AUTOFILL_ITEMS' // New command for autofilling items
};



// Multi-Select Product Modal Component
const ProductMultiSelectModal = ({ 
  open, 
  onClose, 
  products, 
  onConfirm,
  onSaveToTable,
  onAutofill 
}) => {

  console.log('Products passed to ProductMultiSelectModal:', products);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isSaving, setIsSaving] = useState(false);
  const listRef = useRef(null);
  const searchInputRef = useRef(null);
  const tableBodyRef = useRef(null);

  // Filter products based on search term
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products;
    const term = searchTerm.toLowerCase();
    return products.filter(product => {
      // Search in all specified fields
      return (
        (product.ProductCode && product.ProductCode.toLowerCase().includes(term)) || 
        (product.ProductName && product.ProductName.toLowerCase().includes(term)) ||
        (product.Status && product.Status.toLowerCase().includes(term)) ||
        (product.CategoryName && product.CategoryName.toLowerCase().includes(term)) ||
        (product.UOMName && product.UOMName.toLowerCase().includes(term)) ||
        (product.Description && product.Description.toLowerCase().includes(term)) ||
        (product.BranchName && product.BranchName.toLowerCase().includes(term)) ||
        (product.DepartmentName && product.DepartmentName.toLowerCase().includes(term)) ||
        (product.AllowProductNegativeInventory !== undefined && 
         (product.AllowProductNegativeInventory ? 'yes' : 'no').includes(term)) ||
        (product.AllowBatchOnProduct !== undefined && 
         (product.AllowBatchOnProduct ? 'yes' : 'no').includes(term)) ||
        (product.ProductType && product.ProductType.toLowerCase().includes(term)) ||
        (product.ProductionBOMType && product.ProductionBOMType.toLowerCase().includes(term))
      );
    });
  }, [products, searchTerm]);

  // Check if all filtered products are selected
  const allFilteredSelected = useMemo(() => {
    if (filteredProducts.length === 0) return false;
    return filteredProducts.every(product => selectedProducts.includes(product.ProductCode));
  }, [filteredProducts, selectedProducts]);

  // Check if some filtered products are selected (for indeterminate state)
  const someFilteredSelected = useMemo(() => {
    if (filteredProducts.length === 0) return false;
    return filteredProducts.some(product => selectedProducts.includes(product.ProductCode)) && !allFilteredSelected;
  }, [filteredProducts, selectedProducts, allFilteredSelected]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (open && tableBodyRef.current && highlightedIndex >= 0) {
      const items = tableBodyRef.current.querySelectorAll('.product-row');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, open]);

  // Reset and focus when modal opens
  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setSelectedProducts([]);
      setHighlightedIndex(-1);
      
      // Focus search input after a short delay
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  }, [open]);

  // Handle keyboard navigation
 // Update the handleKeyDown function in ProductMultiSelectModal component
const handleKeyDown = (e) => {
  // Handle search input keys
  if (e.target === searchInputRef.current) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(0);
      // Move focus to the table body
      setTimeout(() => {
        if (tableBodyRef.current) {
          tableBodyRef.current.focus();
        }
      }, 0);
      return;
    }
    
    // Handle Enter key in search input
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // Check if Ctrl key is pressed for autofill
      if (e.ctrlKey) {
        // Ctrl+Enter for autofill
        if (selectedProducts.length > 0) {
          handleAutofill();
        } else {
          // If no products selected, select the first one and autofill
          if (filteredProducts.length > 0) {
            setSelectedProducts([filteredProducts[0].ProductCode]);
            setTimeout(() => handleAutofill(), 0);
          }
        }
      } else {
        // Regular Enter for selecting top product
        if (filteredProducts.length > 0) {
          toggleProductSelection(filteredProducts[0].ProductCode);
          // Clear search and reset for next search
          setSearchTerm('');
          setHighlightedIndex(-1);
          // Keep focus on search input
          setTimeout(() => {
            if (searchInputRef.current) {
              searchInputRef.current.focus();
            }
          }, 0);
        }
      }
      return;
    }
    return;
  }

  // Handle table body navigation
  if (e.target === tableBodyRef.current) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev < filteredProducts.length - 1) {
            return prev + 1;
          } else if (filteredProducts.length > 0) {
            return 0; // Loop back to first item
          }
          return prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev > 0) {
            return prev - 1;
          } else if (filteredProducts.length > 0) {
            return filteredProducts.length - 1; // Loop to last item
          }
          return prev;
        });
        break;
      case ' ':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredProducts.length) {
          toggleProductSelection(filteredProducts[highlightedIndex].ProductCode);
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (e.ctrlKey) {
          // Ctrl+Enter for autofill
          handleAutofill();
        } else if (highlightedIndex >= 0 && highlightedIndex < filteredProducts.length) {
          // Regular Enter for selecting highlighted product
          toggleProductSelection(filteredProducts[highlightedIndex].ProductCode);
        }
        break;
      case 'Escape':
        onClose();
        break;
      default:
        break;
    }
  }
};

  // Toggle product selection
  const toggleProductSelection = (productCode) => {
    setSelectedProducts(prev => 
      prev.includes(productCode)
        ? prev.filter(code => code !== productCode)
        : [...prev, productCode]
    );
  };

  // Handle select all filtered products
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Add all filtered products to selection
      const allProductCodes = filteredProducts.map(p => p.ProductCode);
      setSelectedProducts(prev => [...new Set([...prev, ...allProductCodes])]);
    } else {
      // Remove all filtered products from selection
      const filteredProductCodes = filteredProducts.map(p => p.ProductCode);
      setSelectedProducts(prev => prev.filter(code => !filteredProductCodes.includes(code)));
    }
  };

  // Handle clear all selection
  const handleClearAll = () => {
    setSelectedProducts([]);
  };

  // Handle confirm selection
  const handleConfirm = () => {
    onConfirm(selectedProducts);
    onClose();
  };

  // Handle save to table
  const handleSaveToTable = async () => {
    if (selectedProducts.length === 0) return;
    
    setIsSaving(true);
    try {
      await onSaveToTable(selectedProducts);
      alert('Items saved to table successfully!');
    } catch (error) {
      console.error('Error saving to table:', error);
      alert('Failed to save items to table');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Handle autofill
  const handleAutofill = () => {
    if (selectedProducts.length === 0) return;
    
    try {
      const result = onAutofill(selectedProducts);
      
      if (result && result.success) {
        // Success
      } else {
        alert(result?.error || 'Error autofilling table. Please try again.');
      }
    } catch (error) {
      console.error('Error during autofill:', error);
      alert('Error autofilling table. Please try again.');
    } finally {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-2xl w-full  h-[100vh] flex flex-col border border-gray-200">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-800">Select Multiple Products</h3>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded-full hover:bg-gray-200"
            >
              <CloseIcon size={24} />
            </button>
          </div>
        </div>
        
        <div className="p-5 border-b border-gray-200 bg-white">
          <div className="relative">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by Product Code, Name, Status, Category, UOM, Description, Branch, Department, etc..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base shadow-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <div className="absolute right-3 top-3 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          <div className="mt-3 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-100">
            <div className="font-medium text-blue-800 mb-1">Keyboard Shortcuts:</div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">↑</kbd>
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">↓</kbd>
                <span className="ml-1">Navigate</span>
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Space</kbd>
                <span className="ml-1">Select</span>
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Enter</kbd>
                <span className="ml-1">Select</span>
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Ctrl</kbd>
                <span className="mx-1">+</span>
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Enter</kbd>
                <span className="ml-1">Autofill</span>
              </span>
            </div>
          </div>
        </div>
        
        <div 
          ref={listRef}
          className="flex-grow overflow-y-auto bg-gray-50 relative"
        >
          {filteredProducts.length > 0 ? (
            <div className="h-full flex flex-col">
              {/* Single Table with Sticky Header */}
              <div className="flex-grow overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  {/* Sticky Header */}
                  <thead className="sticky top-0 z-30 bg-gray-100 shadow-md">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-12">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                          checked={allFilteredSelected}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = someFilteredSelected;
                            }
                          }}
                          onChange={handleSelectAll}
                          title={allFilteredSelected ? "Deselect all" : someFilteredSelected ? "Select all filtered" : "Select all filtered"}
                        />
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
                        Product Code
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider min-w-[200px]">
                        Product Name
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-24">
                        Status
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Category
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-20">
                        UOM
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider min-w-[180px]">
                        Description
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Branch
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Department
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
                        Negative Inv.
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-24">
                        Batch
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Product Type
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        BOM Type
                      </th>
                    </tr>
                  </thead>
                  
                  {/* Scrollable Body */}
                  <tbody 
                    ref={tableBodyRef}
                    className="bg-white divide-y divide-gray-200 focus:outline-none"
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                  >
                    {filteredProducts.map((product, index) => (
                      <tr
                        key={product.ProductCode}
                        className={`product-row hover:bg-blue-50 transition-colors ${
                          highlightedIndex === index ? 'bg-blue-100' : ''
                        } ${selectedProducts.includes(product.ProductCode) ? 'bg-blue-50' : ''}`}
                        onClick={() => toggleProductSelection(product.ProductCode)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                            checked={selectedProducts.includes(product.ProductCode)}
                            onChange={() => {}}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 w-32">
                          {product.ProductCode}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium min-w-[200px]">
                          {product.ProductName}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm w-24">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            product.Status === 'Active' ? 'bg-green-100 text-green-800' : 
                            product.Status === 'Inactive' ? 'bg-red-100 text-red-800' : 
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {product.Status || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.CategoryName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-20">
                          {product.UOMName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate min-w-[180px]" title={product.Description || ''}>
                          {product.Description || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.BranchName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.DepartmentName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm w-32">
                          {product.AllowProductNegativeInventory !== undefined ? (
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              product.AllowProductNegativeInventory ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {product.AllowProductNegativeInventory ? 'Yes' : 'No'}
                            </span>
                          ) : 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm w-24">
                          {product.AllowBatchOnProduct !== undefined ? (
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              product.AllowBatchOnProduct ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {product.AllowBatchOnProduct ? 'Yes' : 'No'}
                            </span>
                          ) : 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.ProductType || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.ProductionBOMType || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-16">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No products found</h3>
              <p className="mt-2 text-sm text-gray-500">
                {products.length === 0 
                  ? 'No products are available in the system.' 
                  : 'No products match your search criteria. Try different keywords.'}
              </p>
            </div>
          )}
        </div>
        
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-700">
              <span className="font-medium">{selectedProducts.length}</span> product{selectedProducts.length !== 1 ? 's' : ''} selected
            </div>
            {selectedProducts.length > 0 && (
              <button
                type="button"
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                onClick={handleClearAll}
              >
                Clear All
              </button>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={handleSaveToTable}
              disabled={selectedProducts.length === 0 || isSaving}
            >
              <SaveIcon className="mr-2 h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save to Table'}
            </button>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={handleAutofill}
              disabled={selectedProducts.length === 0}
            >
              <AutofillIcon className="mr-2 h-4 w-4" />
              Autofill Table
            </button>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              onClick={handleConfirm}
              disabled={selectedProducts.length === 0}
            >
              Add Selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};





// Reusable Searchable Dropdown Component
// Reusable Searchable Dropdown Component with Auto-Focus on Search
const SearchableDropdown = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select",
  error = false,
  width = "w-full",
  getOptionLabel = (option) => option.name || option.label || option.toString(),
  getOptionValue = (option) => option.value || option.id || option.toString(),
  getOptionSecondaryLabel = (option) => option.code || option.id || "",
  disabled = false,
  className = "",
  onMultiSelectTrigger = null,
  multiSelectShortcut = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownListRef = useRef(null);
  const searchInputRef = useRef(null);

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  }, [isOpen]);

  // Handle window resize and scroll to update position
  useEffect(() => {
    if (!isOpen) return;
    
    const handleUpdatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        });
      }
    };
    
    window.addEventListener('resize', handleUpdatePosition);
    window.addEventListener('scroll', handleUpdatePosition);
    
    return () => {
      window.removeEventListener('resize', handleUpdatePosition);
      window.removeEventListener('scroll', handleUpdatePosition);
    };
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && dropdownListRef.current && highlightedIndex >= 0) {
      const items = dropdownListRef.current.querySelectorAll('.dropdown-item');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Clear search term and reset highlighted index when opening
      setSearchTerm('');
      setHighlightedIndex(-1);
      
      // Focus the search input after a short delay to ensure it's rendered
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 0);
    }
  }, [isOpen]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    
    return options.filter(option => {
      const label = getOptionLabel(option).toLowerCase();
      const secondaryLabel = getOptionSecondaryLabel(option).toLowerCase();
      
      return label.includes(term) || secondaryLabel.includes(term);
    });
  }, [options, searchTerm, getOptionLabel, getOptionSecondaryLabel]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // Check for multi-select shortcut
    if (onMultiSelectTrigger && multiSelectShortcut && e.ctrlKey && e.key === multiSelectShortcut) {
      e.preventDefault();
      onMultiSelectTrigger();
      return;
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev < filteredOptions.length - 1) {
            return prev + 1;
          } else if (filteredOptions.length > 0) {
            return 0; // Loop back to first item
          }
          return prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev > 0) {
            return prev - 1;
          } else if (filteredOptions.length > 0) {
            return filteredOptions.length - 1; // Loop to last item
          }
          return prev;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleSelectOption = (option) => {
    onChange(getOptionValue(option));
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get selected option
  const selectedOption = options.find(option => getOptionValue(option) === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`${width} border rounded-lg px-3 py-2 cursor-pointer flex justify-between items-center ${
          error ? 'border-red-400' : 'border-[#E2E8F0]'
        } ${isOpen ? 'ring-1 ring-[#BDDDFC]' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        ref={triggerRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <span className="truncate">
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {isOpen && (
        <div 
          className="fixed z-[9999] bg-white border border-[#E2E8F0] rounded-lg shadow-lg"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="p-2 border-b border-[#E2E8F0]">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              className="w-full px-2 py-1 border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          
          <div 
            className="max-h-60 overflow-y-auto" 
            ref={dropdownListRef}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={getOptionValue(option)}
                  className={`dropdown-item px-3 py-2 cursor-pointer hover:bg-[#F1F5F9] ${
                    highlightedIndex === index ? 'bg-[#BDDDFC]' : ''
                  } ${value === getOptionValue(option) ? 'bg-[#E6F0FF]' : ''}`}
                  onClick={() => handleSelectOption(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="font-medium">{getOptionLabel(option)}</div>
                  {getOptionSecondaryLabel(option) && (
                    <div className="text-xs text-gray-500">{getOptionSecondaryLabel(option)}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">No options found</div>
            )}
          </div>
        </div>
      )}
      
      {error && <span className="text-xs text-red-500">*</span>}
    </div>
  );
};

// Multi-Select Warehouse Modal Component


const SalesOrderFormModal = ({ open, onClose, onSubmit, initialData, isEdit }) => {
  const [header, setHeader] = useState(initialHeader);
  const [items, setItems] = useState([{ ...initialItem }]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isUserValid, setIsUserValid] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [dropdownData, setDropdownData] = useState({
    customers: [],
    currencies: [],
    locations: [], // Added locations to dropdown data
    taxCodes: [],
    products: [],
    statusOptions: [],
    branches: [], // Add this line
    nextSONumber: '',
    nextItemCode: ''
  });
  const [isMaximized, setIsMaximized] = useState(false);
  
  // State for selected customer addresses and contacts
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [billingAddresses, setBillingAddresses] = useState([]);
  const [shippingAddresses, setShippingAddresses] = useState([]);
  const [customerContacts, setCustomerContacts] = useState([]);
  const [selectedBillingAddress, setSelectedBillingAddress] = useState(null);
  const [selectedShippingAddress, setSelectedShippingAddress] = useState(null);
  const [selectedCustomerContact, setSelectedCustomerContact] = useState(null);
  
  // Department-related state
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [screenConfig, setScreenConfig] = useState(null);
  const [showDepartmentField, setShowDepartmentField] = useState(false);
  const [showDepartmentItemField, setShowDepartmentItemField] = useState(false);
  
  // Multi-select modals state
  const [showMultiSelectModal, setShowMultiSelectModal] = useState(false);
  const [savedItems, setSavedItems] = useState([]); // State for saved items from table
  
  // Function to toggle maximize/minimize
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
  // Function to fetch current user
  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('authToken'); // store token after login
      if (!token) {
        setIsUserValid(false);
        setAuthError('No token found. Please log in again.');
        return false;
      }
      const userRes = await fetch(`${API_URL}/current-user`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const userData = await userRes.json();
      
      if (userData.user && userData.user.UserID) {
        console.log('Setting current user:', userData.user);
        setCurrentUser(userData.user);
        setIsUserValid(true);
        setAuthError(null);
        return true;
      } else {
        console.error('Invalid user data received:', userData);
        setIsUserValid(false);
        setAuthError('User information not available. Please log in again.');
        return false;
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      setIsUserValid(false);
      setAuthError('Error fetching user information. Please refresh the page.');
      return false;
    }
  };
  
  // Function to fetch departments
  const fetchDepartments = async () => {
    setLoadingDepartments(true);
    try {
      const response = await fetch(`${API_URL}/departments`);
      const data = await response.json();
      console.log('Fetched departments:', data);
      setDepartments(data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };
  
  // Function to fetch screen configuration
  const fetchScreenConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/screens`);
      const data = await response.json();
      console.log('Fetched screen configurations:', data);
      
      // Find the Sales Order screen configuration
      const soScreen = data.screens.find(screen => 
        screen.ScreenName.toLowerCase() === 'SalesOrder'.toLowerCase()
      );
      
      console.log('Sales Order screen configuration:', soScreen);
      if (soScreen) {
        setScreenConfig(soScreen);
        // Show department field if ShowOnHeader is true
        const shouldShowDepartment = soScreen.ShowOnHeader === true;
        setShowDepartmentField(shouldShowDepartment);
        
        // Show department item field if ShowOnItem is true
        const shouldShowDepartmentItem = soScreen.ShowOnItem === true;
        setShowDepartmentItemField(shouldShowDepartmentItem);
        
        // If department fields should be shown, fetch departments
        if (shouldShowDepartment || shouldShowDepartmentItem) {
          fetchDepartments();
        }
      }
    } catch (error) {
      console.error('Error fetching screen configuration:', error);
      setShowDepartmentField(false);
      setShowDepartmentItemField(false);
    }
  };
  
  // ERP Command: Save popup items to table
  const executeERPCommand = async (command, data) => {
    try {
      const response = await fetch(`${API_URL}/erp/execute-command`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          command,
          data
        })
      });
      
      if (!response.ok) {
        throw new Error(`ERP command failed: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error executing ERP command ${command}:`, error);
      throw error;
    }
  };
  
  // Save selected products to table
  const savePopupItemsToTable = async (selectedProductCodes) => {
    const productsToSave = selectedProductCodes.map(productCode => {
      const product = dropdownData.products.find(p => p.ProductCode === productCode);
      return {
        productCode: product.ProductCode,
        productName: product.ProductName,
        uom: product.UOMName || product.UOMID || '',
        savedDate: new Date().toISOString()
      };
    });
    
    return await executeERPCommand(ERP_COMMANDS.SAVE_POPUP_ITEMS, {
      soNumber: header.soNumber,
      items: productsToSave
    });
  };
  
  // Load saved items from table
  const loadSavedItemsFromTable = async () => {
    try {
      const result = await executeERPCommand(ERP_COMMANDS.LOAD_SAVED_ITEMS, {
        soNumber: header.soNumber
      });
      
      if (result && result.items && result.items.length > 0) {
        setSavedItems(result.items);
        
        // Convert saved items to form items
        const formItems = result.items.map(savedItem => ({
          ...initialItem,
          productCode: savedItem.productCode,
          productName: savedItem.productName,
          uomID: savedItem.uom,
          savedToTable: true
        }));
        
        setItems(formItems);
      }
    } catch (error) {
      console.error('Error loading saved items:', error);
    }
  };
  
  // Delete saved items from table
  const deleteSavedItemsFromTable = async () => {
    try {
      await executeERPCommand(ERP_COMMANDS.DELETE_SAVED_ITEMS, {
        soNumber: header.soNumber
      });
      setSavedItems([]);
    } catch (error) {
      console.error('Error deleting saved items:', error);
    }
  };
  
  // Autofill selected products into the table
  const autofillSelectedProducts = (selectedProductCodes) => {
    if (selectedProductCodes.length === 0) return;
    
    try {
      const newItems = selectedProductCodes.map(productCode => {
        const product = dropdownData.products.find(p => p.ProductCode === productCode);
        if (!product) return { ...initialItem };
        
        return {
          ...initialItem,
          productCode: product.ProductCode,
          productName: product.ProductName,
          uomID: product.UOMID || '',
          description: product.Description || '',
          department_id: showDepartmentItemField ? "" : undefined,
        };
      });
      
      // Append new items to existing items instead of replacing them
      setItems(prevItems => [...prevItems, ...newItems]);
      
      // Return success for the modal to show notification
      return { success: true, count: selectedProductCodes.length };
    } catch (error) {
      console.error('Error in autofillSelectedProducts:', error);
      // Return error for the modal to show error message
      return { success: false, error: error.message };
    }
  };
  
  // Handle multi-select products
  const handleMultiSelectProducts = (selectedProductCodes) => {
    if (selectedProductCodes.length === 0) return;
    
    const newItems = selectedProductCodes.map(productCode => {
      const product = dropdownData.products.find(p => p.ProductCode === productCode);
      if (!product) return { ...initialItem };
      
      return {
        ...initialItem,
        productCode: product.ProductCode,
        productName: product.ProductName,
        uomID: product.UOMID || '',
        description: product.Description || '',
        department_id: showDepartmentItemField ? "" : undefined,
      };
    });
    
    // Append new items to existing items instead of replacing them
    setItems(prevItems => [...prevItems, ...newItems]);
  };
  
  // Handle multi-select warehouses
  // const handleMultiSelectWarehouses = (selectedWarehouseCodes) => {
  //   if (selectedWarehouseCodes.length === 0) return;
    
  //   // For now, just use the first selected warehouse
  //   // You can modify this logic based on your requirements
  //   const firstWarehouseCode = selectedWarehouseCodes[0];
    
  //   // Update header to use the selected warehouse
  //   setHeader(prev => ({
  //     ...prev,
  //     locationCode: firstWarehouseCode
  //   }));
  // };
  
  // Function to calculate tax for an item
const calculateItemTax = (item) => {
  const quantity = parseFloat(item.quantity) || 0;
  const unitPrice = parseFloat(item.unitPrice) || 0;
  const discount = parseFloat(item.discount) || 0;
  
  // Calculate item subtotal before discount
  const itemSubtotal = quantity * unitPrice;
  const discountAmount = discount; // Sales discount is flat amount, not percentage
  
  // Calculate tax for this item
  let taxAmount = 0;
  let taxRate = 0;
  
  console.log('Calculating tax for item:', item);
  console.log('Available tax codes:', dropdownData.taxCodes);
  
  if (item.taxCode && dropdownData.taxCodes.length > 0) {
    // Try different possible field names for tax code
    const taxCode = dropdownData.taxCodes.find(t => {
      const code = t.TaxCode || t.taxCode || t.code || '';
      console.log(`Comparing tax code ${code} with item tax code ${item.taxCode}`);
      return code === item.taxCode;
    });
    
    console.log('Found tax code object:', taxCode);
    
    if (taxCode) {
      // Try different possible field names for tax percentage
      taxRate = parseFloat(taxCode.TaxPercentage || taxCode.taxPercentage || taxCode.Rate || taxCode.rate || 0);
      console.log('Tax rate:', taxRate);
      // Apply tax on amount after discount
      const taxableAmount = itemSubtotal - discountAmount;
      taxAmount = taxableAmount * (taxRate / 100);
      console.log('Tax amount:', taxAmount);
    }
  }
  
  // Calculate item total (subtotal - discount + tax)
  const total = itemSubtotal - discountAmount + taxAmount;
  console.log('Total:', total);
  
  return {
    total: total.toFixed(2),
    taxAmount: taxAmount.toFixed(2),
    taxRate: taxRate
  };
};
  
  // Function to recalculate all items when tax codes change
  const recalculateAllItems = () => {
    const updatedItems = items.map(item => {
      const { total, taxAmount, taxRate } = calculateItemTax(item);
      return {
        ...item,
        total,
        taxAmount,
        taxRate: taxRate.toString()
      };
    });
    setItems(updatedItems);
  };
  
  // Recalculate items when dropdown data (tax codes) changes
useEffect(() => {
  // Recalculate all items when tax codes are loaded
  if (dropdownData.taxCodes.length > 0) {
    console.log('Tax codes loaded, recalculating all items');
    const updatedItems = items.map(item => {
      const { total, taxAmount, taxRate } = calculateItemTax(item);
      return {
        ...item,
        total,
        taxAmount,
        taxRate: taxRate.toString()
      };
    });
    setItems(updatedItems);
  }
}, [dropdownData.taxCodes]);
  
  // Calculate summary values
  const summary = useMemo(() => {
    let subtotal = 0;
    let totalDiscount = 0;
    let totalTax = 0;
    
    items.forEach(item => {
      const quantity = parseFloat(item.quantity) || 0;
      const unitPrice = parseFloat(item.unitPrice) || 0;
      const discount = parseFloat(item.discount) || 0;
      
      // Calculate item total before discount
      const itemSubtotal = quantity * unitPrice;
      const discountAmount = (discount / 100) * itemSubtotal; // Sales discount is flat amount
      subtotal += itemSubtotal;
      totalDiscount += discountAmount;
      
      // Calculate tax for this item
      if (item.taxCode) {
        const taxCode = dropdownData.taxCodes.find(t => t.TaxCode === item.taxCode);
        if (taxCode && taxCode.Rate) {
          // Apply tax on amount after discount
          const taxableAmount = itemSubtotal - discountAmount;
          const taxAmount = taxableAmount * (parseFloat(taxCode.Rate) / 100);
          totalTax += taxAmount;
        }
      }
    });
    
    const grandTotal = subtotal - totalDiscount + totalTax;
    
    return {
      subtotal: subtotal.toFixed(2),
      totalDiscount: totalDiscount.toFixed(2),
      totalTax: totalTax.toFixed(2),
      grandTotal: grandTotal.toFixed(2)
    };
  }, [items, dropdownData.taxCodes]);
  
  // Update header when summary changes
  useEffect(() => {
    setHeader(prev => ({
      ...prev,
      SubTotal: summary.subtotal,
      TotalDiscount: summary.totalDiscount,
      TotalTax: summary.totalTax,
      TotalAmount: summary.grandTotal
    }));
  }, [summary]);
  
  // Fetch current user on mount
  useEffect(() => {
    fetchCurrentUser();
  }, []);
  
  // Handle keyboard shortcuts for multi-select modals
 useEffect(() => {
  if (!open) return;

  const handleKeyDown = (e) => {
    // Check for Alt+P - Multi-select products
    if (e.altKey && e.key === 'p') {
      e.preventDefault();
      setShowMultiSelectModal(true);
      return;
    }
    
    // Check for Alt+W - Multi-select warehouses
   
    
    // Check for Alt+C - Focus customer dropdown
    if (e.altKey && e.key === 'c') {
      console.log('Alt+C pressed');
      e.preventDefault();
      // Find and focus the customer dropdown
      const customerDropdown = document.querySelector('[data-customer-dropdown]');
      if (customerDropdown) {
        customerDropdown.click();
      }
      return;
    }
    
    // Check for Alt+T - Focus tax dropdown
    if (e.altKey && e.key === 't') {
      e.preventDefault();
      // Find and focus the first tax dropdown
      const taxDropdown = document.querySelector('[data-tax-dropdown]');
      if (taxDropdown) {
        taxDropdown.click();
      }
      return;
    }
  };

  document.addEventListener('keydown', handleKeyDown);

  return () => {
    document.removeEventListener('keydown', handleKeyDown);
  };
}, [open]);
  
const fetchCustomerAddresses = async (customerCode) => {
  try {
    const response = await fetch(`${API_URL}/customers/${customerCode}/addresses`);
    if (!response.ok) throw new Error("Failed to fetch customer addresses");
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching customer addresses:", error);
    return { shipping_addresses: [], billing_addresses: [] };
  }
};

const fetchCustomerContacts = async (customerCode) => {
  try {
    const response = await fetch(`${API_URL}/customers/${customerCode}/contacts`);
    if (!response.ok) throw new Error("Failed to fetch customer contacts");
    const data = await response.json();
    console.log('Fetched customer contacts:', data); // Log the data structure
    return data;
  } catch (error) {
    console.error("Error fetching customer contacts:", error);
    return [];
  }
};

  // Handle customer change to update addresses and contacts
 useEffect(() => {
  const fetchCustomerData = async () => {
    if (header.customerCode) {
      // Find the basic customer info from the dropdown data
      const customer = dropdownData.customers.find(c => c.customer_code === header.customerCode);
      setSelectedCustomer(customer);
      
      if (customer) {
        try {
          // Fetch addresses and contacts for this customer
          const [addressesData, contactsData] = await Promise.all([
            fetchCustomerAddresses(header.customerCode),
            fetchCustomerContacts(header.customerCode)
          ]);
          
          // Set the addresses and contacts
          setBillingAddresses(addressesData.billing_addresses || []);
          setShippingAddresses(addressesData.shipping_addresses || []);
          setCustomerContacts(contactsData || []);
          
          // Reset selected addresses if they don't belong to this customer
          if (!addressesData.billing_addresses?.some(addr => addr.address_id === parseInt(header.billingAddressID))) {
            setHeader(prev => ({ ...prev, billingAddressID: '' }));
            setSelectedBillingAddress(null);
          }
          
          if (!addressesData.shipping_addresses?.some(addr => addr.address_id === parseInt(header.shippingAddressID))) {
            setHeader(prev => ({ ...prev, shippingAddressID: '' }));
            setSelectedShippingAddress(null);
          }
          
          // Reset selected contact if it doesn't belong to this customer
          if (!contactsData?.some(contact => contact.contact_id === parseInt(header.customerContactID))) {
            setHeader(prev => ({ ...prev, customerContactID: '' }));
            setSelectedCustomerContact(null);
          }
        } catch (error) {
          console.error('Error fetching customer data:', error);
          // Set empty arrays on error
          setBillingAddresses([]);
          setShippingAddresses([]);
          setCustomerContacts([]);
          setSelectedBillingAddress(null);
          setSelectedShippingAddress(null);
          setSelectedCustomerContact(null);
        }
      } else {
        // Customer not found in dropdown data
        setBillingAddresses([]);
        setShippingAddresses([]);
        setCustomerContacts([]);
        setSelectedBillingAddress(null);
        setSelectedShippingAddress(null);
        setSelectedCustomerContact(null);
      }
    } else {
      // No customer selected
      setSelectedCustomer(null);
      setBillingAddresses([]);
      setShippingAddresses([]);
      setCustomerContacts([]);
      setSelectedBillingAddress(null);
      setSelectedShippingAddress(null);
      setSelectedCustomerContact(null);
    }
  };
  
  fetchCustomerData();
}, [header.customerCode, dropdownData.customers]);
  
  // Handle billing address change
  useEffect(() => {
    if (header.billingAddressID && billingAddresses.length > 0) {
      const address = billingAddresses.find(addr => addr.address_id === parseInt(header.billingAddressID));
      setSelectedBillingAddress(address);
    } else {
      setSelectedBillingAddress(null);
    }
  }, [header.billingAddressID, billingAddresses]);
  
  // Handle shipping address change
  useEffect(() => {
    if (header.shippingAddressID && shippingAddresses.length > 0) {
      const address = shippingAddresses.find(addr => addr.address_id === parseInt(header.shippingAddressID));
      setSelectedShippingAddress(address);
    } else {
      setSelectedShippingAddress(null);
    }
  }, [header.shippingAddressID, shippingAddresses]);
  
  // Handle customer contact change
  useEffect(() => {
    if (header.customerContactID && customerContacts.length > 0) {
      const contact = customerContacts.find(contact => contact.contact_id === parseInt(header.customerContactID));
      setSelectedCustomerContact(contact);
    } else {
      setSelectedCustomerContact(null);
    }
  }, [header.customerContactID, customerContacts]);
  
  useEffect(() => {
    if (open) {
      // Fetch current user when modal opens
      fetchCurrentUser();
      fetchScreenConfig(); // Add this
      fetchDropdownData();
      
      // Load saved items from table for new records
      if (!isEdit) {
        loadSavedItemsFromTable();
      }
      
      if (isEdit && initialData) {
        // Normalize payment term to match one of the options
        const paymentTerm = initialData.PaymentTerm || '';
        const normalizedPaymentTerm = PAYMENT_TERMS_OPTIONS.find(
          option => option.value && option.value.toLowerCase() === paymentTerm.toLowerCase()
        )?.value || '';
        
        // Set header data
        setHeader({
          soNumber: initialData.SalesOrderNumber || '',
          soDate: initialData.SalesOrderDate ? new Date(initialData.SalesOrderDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          customerCode: initialData.CustomerCode || '',
          currencyCode: initialData.CurrencyCode || '',
          deliveryDate: initialData.DeliveryDate ? new Date(initialData.DeliveryDate).toISOString().split('T')[0] : '',
          paymentTerms: normalizedPaymentTerm,
          remarks: initialData.Remarks || '',
          locationCode: initialData.LocationCode || '', // Added locationCode to header
          billingAddressID: initialData.BillingAddressID ? initialData.BillingAddressID.toString() : '', // Added billing address ID
          shippingAddressID: initialData.ShippingAddressID ? initialData.ShippingAddressID.toString() : '', // Added shipping address ID
          customerContactID: initialData.CustomerContactID ? initialData.CustomerContactID.toString() : '', // Added customer contact ID
          customerOrderNumber: initialData.CustomerOrderNumber || '', // NEW
          branchCode: initialData.BranchCode || '', // Add this line
          customerOrderDate: initialData.CustomerOrderDate ? new Date(initialData.CustomerOrderDate).toISOString().split('T')[0] : '', // NEW
          customerDueDate: initialData.CustomerDueDate ? new Date(initialData.CustomerDueDate).toISOString().split('T')[0] : '', // NEW
          IsActive: initialData.IsActive?.toString() || '1',
          SubTotal: initialData.SubTotal?.toString() || '0',
          TotalDiscount: initialData.TotalDiscount?.toString() || '0',
          TotalTax: initialData.TotalTax?.toString() || '0',
          TotalAmount: initialData.TotalAmount?.toString() || '0',
          department_id: initialData.DepartmentID?.toString() || '', // Add department_id
        });
        
        // Set items data
        if (initialData.Items && initialData.Items.length > 0) {
          setItems(initialData.Items.map(item => {
            // Calculate remaining quantity if not provided
            const quantity = parseFloat(item.Quantity) || 0;
            const remainingQty = item.RemainingQty !== undefined && item.RemainingQty !== null 
              ? item.RemainingQty 
              : quantity; // Default to quantity if remaining is not provided
            
            return {
              productCode: item.ProductCode || '',
              productName: item.ProductName || '',
              description: item.Description || '',
              uomID: item.UOMID ? item.UOMID.toString() : '',
              quantity: item.Quantity ? item.Quantity.toString() : '',
              remainingQuantity: remainingQty.toString(),
              dispatchQuantity: item.DispatchQuantity ? item.DispatchQuantity.toString() : '0',
              unitPrice: item.UnitPrice ? item.UnitPrice.toString() : '',
              discount: item.Discount ? item.Discount.toString() : '0',
              taxCode: item.TaxCode || '',
              total: item.Total ? item.Total.toString() : '0',
              taxAmount: item.TaxAmount ? item.TaxAmount.toString() : '0',
              department_id: item.DepartmentID?.toString() || '', // Add department_id
            };
          }));
        } else {
          setItems([{ ...initialItem }]);
        }
      } else {
        setHeader(initialHeader);
        
        // If no saved items, initialize with one empty item
        if (savedItems.length === 0) {
          setItems([{ ...initialItem }]);
        }
      }
      
      setErrors({});
      setIsSubmitting(false);
    }
  }, [open, initialData, isEdit, savedItems.length]);
  
 // Add these functions to your SalesOrderFormModal component

const fetchCustomers = async () => {
  try {
    const response = await fetch(`${API_URL}/customers`);
    if (!response.ok) throw new Error("Failed to fetch customers");
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching customers:", error);
    return [];
  }
};

const fetchCurrencies = async () => {
  try {
    const response = await fetch(`${API_URL}/currencies`);
    if (!response.ok) throw new Error("Failed to fetch currencies");
    const data = await response.json();
    return data.currencies || [];
  } catch (error) {
    console.error("Error fetching currencies:", error);
    return [];
  }
};

const fetchLocations = async () => {
  try {
    const response = await fetch(`${API_URL}/locations`);
    if (!response.ok) throw new Error("Failed to fetch locations");
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching locations:", error);
    return [];
  }
};

const fetchTaxCodes = async () => {
  try {
    const response = await fetch(`${API_URL}/taxes`);
    if (!response.ok) throw new Error("Failed to fetch tax codes");
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching tax codes:", error);
    return [];
  }
};

const fetchProducts = async () => {
  try {
    const response = await fetch(`${API_URL}/products`);
    if (!response.ok) throw new Error("Failed to fetch products");
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching products:", error);
    return [];
  }
};

const fetchBranches = async () => {
  try {
    const response = await fetch(`${API_URL}/branches`);
    if (!response.ok) throw new Error("Failed to fetch branches");
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching branches:", error);
    return [];
  }
};


const fetchNextSONumber = async () => {
  try {
    const response = await fetch(`${API_URL}/sales-order/next-number`);
    if (!response.ok) throw new Error("Failed to fetch next SO number");
    const data = await response.json();
    return data.nextSONumber || "";
  } catch (error) {
    console.error("Error fetching next SO number:", error);
    return "";
  }
};

const fetchNextItemCode = async () => {
  try {
    const response = await fetch(`${API_URL}/sales-order/next-item-code`);
    if (!response.ok) throw new Error("Failed to fetch next item code");
    const data = await response.json();
    return data.nextItemCode || "";
  } catch (error) {
    console.error("Error fetching next item code:", error);
    return "";
  }
};

// Now update the fetchDropdownData function
const fetchDropdownData = async () => {
  console.log('Fetching dropdown data from multiple API endpoints...');
  try {
    setLoading(true);
    
    // Fetch all data in parallel
    const [
      customers,
      currencies,
      locations,
      taxCodes,
      products,
      branches,
      departments,
      screenConfig,
      nextSONumber,
      nextItemCode
    ] = await Promise.all([
      fetchCustomers(),
      fetchCurrencies(),
      fetchLocations(),
      fetchTaxCodes(),
      fetchProducts(),
      fetchBranches(),
      fetchDepartments(),
      fetchScreenConfig(),
      fetchNextSONumber(),
      fetchNextItemCode()
    ]);
    
    // Status options are static, so we don't need to fetch them
    const statusOptions = [
      { value: '1', label: 'Active' },
      { value: '0', label: 'Inactive' },
    ];
    
    setDropdownData({
      customers,
      currencies,
      locations,
      taxCodes,
      products,
      branches,
      statusOptions,
      nextSONumber,
      nextItemCode,
      departments,
      screenConfig
    });

    console.log('Fetched dropdown data:', products);
    
    // Set SO number if creating new
    if (!isEdit) {
      setHeader(prev => ({ 
        ...prev, 
        soNumber: nextSONumber || '',
        IsActive: '1', // Default to Active
        branchCode: '' // Explicitly set to empty string
      }));
    }
    
    // Process screen config
    if (screenConfig) {
      setScreenConfig(screenConfig);
      // Show department field if ShowOnHeader is true
      const shouldShowDepartment = screenConfig.ShowOnHeader === true;
      setShowDepartmentField(shouldShowDepartment);
      
      // Show department item field if ShowOnItem is true
      const shouldShowDepartmentItem = screenConfig.ShowOnItem === true;
      setShowDepartmentItemField(shouldShowDepartmentItem);
    }
  } catch (error) {
    console.error('Error fetching dropdown data:', error);
    alert('Error loading form data');
  } finally {
    setLoading(false);
  }
};
  
  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    setHeader({ ...header, [name]: value });
  };
  
  const handleItemChange = (idx, e) => {
  const { name, value } = e.target;
  const newItems = [...items];
  newItems[idx][name] = value;
  
  // Calculate total when quantity, unit price, discount, or tax code changes
  if (name === 'quantity' || name === 'unitPrice' || name === 'discount' || name === 'taxCode') {
    const { total, taxAmount, taxRate } = calculateItemTax(newItems[idx]);
    newItems[idx].total = total;
    newItems[idx].taxAmount = taxAmount;
    newItems[idx].taxRate = taxRate.toString();
    
    // Update remaining quantity when quantity changes
    if (name === 'quantity') {
      const quantity = parseFloat(value) || 0;
      const dispatchQty = parseFloat(newItems[idx].dispatchQuantity) || 0;
      const remaining = quantity - dispatchQty;
      newItems[idx].remainingQuantity = remaining >= 0 ? remaining.toString() : '0';
    }
  }
  
  setItems(newItems);
};
  
  // Handle product selection - UPDATED to auto-fill UOMID and Product Code
  const handleProductChange = (idx, productCode) => {
    const newItems = [...items];
    
    if (productCode) {
      const selectedProduct = dropdownData.products.find(p => p.ProductCode === productCode);
      if (selectedProduct) {
        // Auto-fill all product-related fields
        newItems[idx] = {
          ...newItems[idx],
          productCode: selectedProduct.ProductCode,
          productName: selectedProduct.ProductName,
          uomID: selectedProduct.UOMID ? selectedProduct.UOMID.toString() : '', // Auto-fill UOMID from product master
          description: selectedProduct.Description || '' // Auto-fill description from product master
        };
        
        // Recalculate totals after product change
        const { total, taxAmount, taxRate } = calculateItemTax(newItems[idx]);
        newItems[idx].total = total;
        newItems[idx].taxAmount = taxAmount;
        newItems[idx].taxRate = taxRate.toString();
      }
    } else {
      // Clear product-related fields if no product is selected
      newItems[idx] = {
        ...newItems[idx],
        productCode: '',
        productName: '',
        uomID: '',
        description: ''
      };
    }
    
    setItems(newItems);
  };
  
  const addItem = () => {
    setItems([...items, { ...initialItem }]);
  };
  
  const removeItem = (idx) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== idx));
    }
  };
  
  const validate = () => {
    const errs = {};
    if (!header.soNumber || !header.soNumber.trim()) errs.soNumber = 'Required';
    if (!header.soDate) errs.soDate = 'Required';
    if (!header.customerCode) errs.customerCode = 'Required';
    if (!header.currencyCode) errs.currencyCode = 'Required';
    if (!header.locationCode) errs.locationCode = 'Required'; // Added location validation
    
    // Validate department field only if it's shown
    if (showDepartmentField && !header.department_id) errs.department_id = 'Required';
    
    items.forEach((item, idx) => {
      if (!item.productName) errs[`productName_${idx}`] = 'Required';
      if (!item.description) errs[`description_${idx}`] = 'Required';
      if (!item.uomID) errs[`uomID_${idx}`] = 'Required';
      if (!item.quantity) errs[`quantity_${idx}`] = 'Required';
      if (!item.unitPrice) errs[`unitPrice_${idx}`] = 'Required';
      if (!item.taxCode) errs[`taxCode_${idx}`] = 'Required';
      
      // Validate department item field only if it's shown
      if (showDepartmentItemField && !item.department_id) errs[`department_id_${idx}`] = 'Required';
    });
    
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || isSubmitting) return;
    
    // Check if we have a valid current user
    if (!isUserValid || !currentUser || !currentUser.UserID) {
      alert(authError || 'User information not available. Please refresh the page and log in again.');
      return;
    }
    
    setIsSubmitting(true);
    setLoading(true);
    
    try {
      // Only check for active orders if creating a new sales order (not editing)
      if (!isEdit && header.customerOrderNumber) {
        try {
          console.log('Checking if customer order number already exists:', header.customerOrderNumber);
          const checkResponse = await fetch(`${API_URL}/sales-order/check-order/${header.customerOrderNumber}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          console.log('Check response status:', checkResponse.status);
          if (checkResponse.ok) {
            const checkData = await checkResponse.json();
            console.log('Check response data:', checkData);
            // Validate response format
            if (checkData && typeof checkData === 'object') {
              if (checkData.orderExists === true) {
                alert(`A sales order with this customer order number (${checkData.existingOrderNumber}) already exists. Please use a unique Customer Order Number.`);
                setIsSubmitting(false);
                setLoading(false);
                return;
              } else if (checkData.orderExists === false) {
                console.log('Customer order number is available for use:', header.customerOrderNumber);
              } else {
                console.warn('Unexpected value for orderExists:', checkData.orderExists);
              }
            } else {
              console.error('Invalid response data format:', checkData);
            }
          } else {
            console.warn('Failed to check for existing customer order number:', checkResponse.status, checkResponse.statusText);
            // Continue with form submission despite the check failure, but log warning
          }
        } catch (checkError) {
          console.error('Error checking for existing customer order number:', checkError);
          // Do not block submission if the check fails; just log error
        }
      }
      
      const payload = {
        SalesOrderNumber: header.soNumber,
        SalesOrderDate: header.soDate,
        CustomerCode: header.customerCode,
        CurrencyCode: header.currencyCode,
        DeliveryDate: header.deliveryDate || null,
        PaymentTerm: header.paymentTerms,
        Remarks: header.remarks,
        LocationCode: header.locationCode,
        BranchCode: header.branchCode,
        BillingAddressID: header.billingAddressID ? parseInt(header.billingAddressID) : null,
        ShippingAddressID: header.shippingAddressID ? parseInt(header.shippingAddressID) : null,
        CustomerContactID: header.customerContactID ? parseInt(header.customerContactID) : null,
        CustomerOrderNumber: header.customerOrderNumber || null,
        CustomerOrderDate: header.customerOrderDate || null,
        CustomerDueDate: header.customerDueDate || null,
        IsActive: parseInt(header.IsActive),
        SubTotal: parseFloat(summary.subtotal),
        TotalDiscount: parseFloat(summary.totalDiscount),
        TotalTax: parseFloat(summary.totalTax),
        TotalAmount: parseFloat(summary.grandTotal),
        CreatedBy: isEdit ? undefined : currentUser.UserID.toString(),
        UpdatedBy: isEdit ? currentUser.UserID.toString() : undefined,
        // Only include department_id if showDepartmentField is true
        ...(showDepartmentField && header.department_id ? { department_id: parseInt(header.department_id) } : {}),
        Items: items.map(item => ({
          ProductName: item.productName,
          ProductCode: item.productCode,
          Description: item.description,
          UOMID: parseInt(item.uomID) || 0,
          Quantity: parseInt(item.quantity) || 0,
          RemainingQty: parseInt(item.remainingQuantity) || 0,
          UnitPrice: parseFloat(item.unitPrice) || 0,
          Discount: parseFloat(item.discount) || 0,
          TaxCode: item.taxCode,
          Total: parseFloat(item.total) || 0,
          Action: isEdit ? 'Update' : 'Add',
          DispatchQuantity: parseFloat(item.dispatchQuantity) || 0,
          // Only include department_id if showDepartmentItemField is true
          ...(showDepartmentItemField && item.department_id ? { department_id: parseInt(item.department_id) } : {}),
        }))
      };
      
      const url = isEdit 
        ? `${API_URL}/sales-order/${header.soNumber}`
        : `${API_URL}/sales-order`;
      
      const method = isEdit ? 'PUT' : 'POST';
      
      console.log('Submitting payload to:', url);
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        throw new Error(errorData.message || 'Failed to save sales order');
      }
      
      const result = await response.json();
      console.log('Success response:', result);
      
      // If creating new, show the generated item codes
      if (!isEdit && result.Items) {
        console.log('Generated item codes:', result.Items.map(item => item.SalesItemCode));
      }
      
      // Delete saved items from table after successful submission
      if (!isEdit) {
        await deleteSavedItemsFromTable();
      }
      
      if (onSubmit) {
        onSubmit();
      }
      
      onClose();
    } catch (err) {
      console.error('Error saving sales order:', err);
      alert(err.message || 'Error saving sales order');
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };
  
  if (!open) return null;
  
  return (
    <>
      <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
        <style>
          {`
            .hide-scrollbar {
              -ms-overflow-style: none;
              scrollbar-width: none;
            }
            .hide-scrollbar::-webkit-scrollbar {
              display: none;
            }
            .custom-scrollbar::-webkit-scrollbar {
              height: 8px;
            }
            .custom-scrollbar::-webkit-scrollbar-track {
              background: #f1f5f9;
              border-radius: 10px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb {
              background: #cbd5e1;
              border-radius: 10px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
              background: #94a3b8;
            }
            .saved-item-badge {
              display: inline-flex;
              align-items: center;
              background-color: #e6f7ff;
              color: #1890ff;
              font-size: 10px;
              padding: 2px 6px;
              border-radius: 10px;
              margin-left: 4px;
            }
            .autofill-button {
              background-color: #f0f9ff;
              color: #0284c7;
              border-color: #0284c7;
            }
            .autofill-button:hover {
              background-color: #e0f2fe;
            }
            .shortcut-key {
              display: inline-block;
              background-color: #f1f5f9;
              color: #334155;
              font-family: monospace;
              font-size: 0.75rem;
              padding: 0.125rem 0.25rem;
              border-radius: 0.25rem;
              border: 1px solid #e2e8f0;
              margin: 0 0.125rem;
            }
          `}
        </style>
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        <div className={`bg-white shadow-2xl w-full h-full flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'} ${isMaximized ? "max-w-full" : "max-w-6xl"}`}>
          {/* Fixed Header */}
          <div className="flex-shrink-0 p-8 pb-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-[#384959]">{isEdit ? 'Edit Sales Order' : 'Create Sales Order'}</h2>
              <div className="flex items-center space-x-4">
                <button
                  className="text-[#A0AEC0] hover:text-[#384959] transition-colors"
                  onClick={toggleMaximize}
                  title={isMaximized ? "Minimize" : "Maximize"}
                >
                  {isMaximized ? <FaCompress size={20} /> : <FaExpand size={20} />}
                </button>
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors" onClick={onClose}>
                  <CloseIcon size={24} />
                </button>
              </div>
            </div>
            
            {/* <div className="mt-4 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
              <div className="font-medium mb-1">Keyboard Shortcuts:</div>
              <div className="grid grid-cols-2 gap-1">
                <div><span className="shortcut-key">Alt+P</span> - Multi-select products</div>
                <div><span className="shortcut-key">Alt+W</span> - Multi-select warehouses</div>
                <div><span className="shortcut-key">Ctrl+P</span> - Multi-select in dropdown</div>
                <div><span className="shortcut-key">Ctrl+W</span> - Multi-select in dropdown</div>
              </div>
              <div className="mt-2 font-medium">ERP Commands:</div>
              <div><span className="shortcut-key">SAVE_POPUP_ITEMS</span> - Save selected items to table</div>
              <div><span className="shortcut-key">LOAD_SAVED_ITEMS</span> - Load saved items from table</div>
              <div><span className="shortcut-key">AUTOFILL_ITEMS</span> - Autofill table with selected items</div>
              <div className="mt-2 font-medium">Multi-Select Modal Commands:</div>
              <div><span className="shortcut-key">Ctrl+Enter</span> - Autofill table with selected products</div>
            </div> */}
          </div>
          
          {/* User validation warning */}
          {open && !isUserValid && (
            <div className="mx-8 mb-4 bg-red-50 border-l-4 border-red-500 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    {authError || 'User information not available. Please refresh the page and log in again.'}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Scrollable Content Area */}
          <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
            <form onSubmit={handleSubmit} className="space-y-6 pb-6">
              {/* SO Header */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <h3 className="text-lg font-semibold text-[#384959] mb-4">Sales Order Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">SO Number *</label>
                    <input 
                      name="soNumber" 
                      value={header.soNumber} 
                      onChange={handleHeaderChange} 
                      disabled
                      className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase"
                    />
                    {errors.soNumber && <span className="text-xs text-red-500">{errors.soNumber}</span>}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">SO Date *</label>
                    <input 
                      type="date" 
                      name="soDate" 
                      value={header.soDate} 
                      onChange={handleHeaderChange} 
                      className={`w-full border rounded-lg px-3 py-2 ${errors.soDate ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase`}
                    />
                    {errors.soDate && <span className="text-xs text-red-500">{errors.soDate}</span>}
                  </div>
                  
                  {/* Customer Selection */}
                  <div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">Customer Name *</label>
  <SearchableDropdown
    options={dropdownData.customers}
    value={header.customerCode}
    onChange={(customerCode) => setHeader(prev => ({ ...prev, customerCode }))}
    getOptionLabel={(customer) => customer.customer_name}
    getOptionValue={(customer) => customer.customer_code}
    getOptionSecondaryLabel={(customer) => customer.customer_code}
    placeholder="Select Customer"
    error={errors.customerCode}
     className="data-customer-dropdown"
  />
  {errors.customerCode && <span className="text-xs text-red-500">{errors.customerCode}</span>}
</div>
                  
                  <div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">Currency *</label>
  <SearchableDropdown
    options={dropdownData.currencies}
    value={header.currencyCode}
    onChange={(currencyCode) => setHeader(prev => ({ ...prev, currencyCode }))}
    getOptionLabel={(currency) => currency.CurrencyName}
    getOptionValue={(currency) => currency.CurrencyCode}
    getOptionSecondaryLabel={(currency) => currency.CurrencyCode}
    placeholder="Select Currency"
    error={errors.currencyCode}
     className="data-currency-dropdown"
  />
  {errors.currencyCode && <span className="text-xs text-red-500">{errors.currencyCode}</span>}
</div>
                  
                  {/* Customer Contact Selection */}
                 <div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">Customer Contact</label>
  <SearchableDropdown
    options={customerContacts}
    value={header.customerContactID}
    onChange={(contactId) => setHeader(prev => ({ ...prev, customerContactID: contactId }))}
    getOptionLabel={(contact) => `${contact.contact_code} ${contact.first_name} ${contact.last_name}`}
    getOptionValue={(contact) => contact.contact_id.toString()}
    getOptionSecondaryLabel={(contact) => contact.contact_email}
    placeholder="Select Contact"
    disabled={!header.customerCode || customerContacts.length === 0}
  />
</div>
                  
                  {/* Customer Contact Display */}
                  {selectedCustomerContact && (
                    <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                      <div className="flex items-center mb-2">
                        <h4 className="text-md font-semibold text-purple-800">Customer Contact</h4>
                      </div>
                      <div className="text-sm text-gray-700 uppercase">
                        <p className="font-medium ">{selectedCustomerContact.first_name} {selectedCustomerContact.last_name}</p>
                        <p>{selectedCustomerContact.email}</p>
                        <p>{selectedCustomerContact.phone_number}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Address Selection Row */}
                 <div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">Billing Address</label>
  <SearchableDropdown
    options={billingAddresses}
    value={header.billingAddressID}
    onChange={(addressId) => setHeader(prev => ({ ...prev, billingAddressID: addressId }))}
    getOptionLabel={(address) => `${address.address_code} - ${address.address_line1}, ${address.city}`}
    getOptionValue={(address) => address.address_id.toString()}
    getOptionSecondaryLabel={(address) => address.address_code}
    placeholder="Select Billing Address"
    disabled={!header.customerCode || billingAddresses.length === 0}
     className="data-billing-dropdown"
  />
</div>
                  
                  <div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">Shipping Address</label>
  <SearchableDropdown
    options={shippingAddresses}
    value={header.shippingAddressID}
    onChange={(addressId) => setHeader(prev => ({ ...prev, shippingAddressID: addressId }))}
    getOptionLabel={(address) => `${address.address_code} - ${address.address_line1}, ${address.city}`}
    getOptionValue={(address) => address.address_id.toString()}
    getOptionSecondaryLabel={(address) => address.address_code}
    placeholder="Select Shipping Address"
    disabled={!header.customerCode || shippingAddresses.length === 0}
    className="data-shipping-dropdown"
  />
</div>
                  
                  {/* Address Details Display Row */}
                  {(selectedBillingAddress || selectedShippingAddress) && (
                    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Billing Address Display */}
                      {selectedBillingAddress && (
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                          <div className="flex items-center mb-2">
                            <h4 className="text-md font-semibold text-blue-800">Billing Address</h4>
                            <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded uppercase">
                              {selectedBillingAddress.address_code}
                            </span>
                          </div>
                          <div className="text-sm text-gray-700 uppercase">
                            <p className="font-medium ">{selectedBillingAddress.contact_person_name}</p>
                            <p>{selectedBillingAddress.contact_person_number}</p>
                            <p className="mt-1 ">{selectedBillingAddress.address_line1}</p>
                            <p>{selectedBillingAddress.city}, {selectedBillingAddress.state} {selectedBillingAddress.zip_code}</p>
                            <p>{selectedBillingAddress.country}</p>
                            {selectedBillingAddress.gst_number && (
                              <p className="mt-1 ">GST: {selectedBillingAddress.gst_number}</p>
                            )}
                            {selectedBillingAddress.pan_number && (
                              <p>PAN: {selectedBillingAddress.pan_number}</p>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Shipping Address Display */}
                      {selectedShippingAddress && (
                        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                          <div className="flex items-center mb-2">
                            <h4 className="text-md font-semibold text-green-800">Shipping Address</h4>
                            <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                              {selectedShippingAddress.address_code}
                            </span>
                          </div>
                          <div className="text-sm text-gray-700 uppercase">
                            <p className="font-medium">{selectedShippingAddress.contact_person_name}</p>
                            <p>{selectedShippingAddress.contact_person_number}</p>
                            <p className="mt-1">{selectedShippingAddress.address_line1}</p>
                            <p>{selectedShippingAddress.city}, {selectedShippingAddress.state} {selectedShippingAddress.zip_code}</p>
                            <p>{selectedShippingAddress.country}</p>
                            {selectedShippingAddress.gst_number && (
                              <p className="mt-1">GST: {selectedShippingAddress.gst_number}</p>
                            )}
                            {selectedShippingAddress.pan_number && (
                              <p>PAN: {selectedShippingAddress.pan_number}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Customer Order Information Section */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Customer Order Number</label>
                    <input 
                      type="text" 
                      name="customerOrderNumber" 
                      value={header.customerOrderNumber} 
                      onChange={handleHeaderChange} 
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                      placeholder="Enter customer PO number"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Customer Order Date</label>
                    <input 
                      type="date" 
                      name="customerOrderDate" 
                      value={header.customerOrderDate} 
                      onChange={handleHeaderChange} 
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Customer Due Date</label>
                    <input 
                      type="date" 
                      name="customerDueDate" 
                      value={header.customerDueDate} 
                      onChange={handleHeaderChange} 
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Delivery Date</label>
                    <input 
                      type="date" 
                      name="deliveryDate" 
                      value={header.deliveryDate} 
                      onChange={handleHeaderChange} 
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                    />
                  </div>
                  
                 <div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">Payment Terms</label>
  <SearchableDropdown
    options={PAYMENT_TERMS_OPTIONS}
    value={header.paymentTerms}
    onChange={(paymentTerm) => setHeader(prev => ({ ...prev, paymentTerms: paymentTerm }))}
    getOptionLabel={(option) => option.label}
    getOptionValue={(option) => option.value}
    placeholder="Select Payment Terms"
    className="data-payment-dropdown"
  />
</div>
                  
                  {/* Added Location field to header */}
                  <div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">Branch *</label>
  <SearchableDropdown
    options={dropdownData.branches}
    value={header.branchCode}
    onChange={(branchCode) => setHeader(prev => ({ ...prev, branchCode }))}
    getOptionLabel={(branch) => `${branch.BranchCode} - ${branch.BranchName}`}
    getOptionValue={(branch) => branch.BranchCode}
    placeholder="Select Branch"
    error={errors.branchCode}
    className="data-branch-dropdown"
  />
  {errors.branchCode && <span className="text-xs text-red-500">{errors.branchCode}</span>}
</div>
                  
                  {/* Department Selection Field - Only shown if ShowOnHeader is true */}
                  {showDepartmentField && (
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Department *
                      </label>
                      <select
                        name="department_id"
                        value={header.department_id}
                        onChange={handleHeaderChange}
                        className={`w-full border rounded-lg px-3 py-2 ${
                          errors.department_id ? "border-red-400" : "border-[#E2E8F0]"
                        } focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase`}
                        disabled={loadingDepartments}
                      >
                        <option value="">{loadingDepartments ? 'Loading departments...' : 'Select Department'}</option>
                        {departments.map((dept) => (
                          <option key={dept.DepartmentID} value={dept.DepartmentID}>
                            {dept.DepartmentCode} - {dept.DepartmentName}
                          </option>
                        ))}
                      </select>
                      {errors.department_id && (
                        <span className="text-xs text-red-500">
                          {errors.department_id}
                        </span>
                      )}
                    </div>
                  )}
                  
                  <div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">Warehouse *</label>
  <SearchableDropdown
    options={dropdownData.locations}
    value={header.locationCode}
    onChange={(locationCode) => setHeader(prev => ({ ...prev, locationCode }))}
    getOptionLabel={(warehouse) => warehouse.LocationName}
    getOptionValue={(warehouse) => warehouse.LocationCode}
    getOptionSecondaryLabel={(warehouse) => warehouse.LocationCode}
    placeholder="Select Warehouse"
    error={errors.locationCode}
    className="data-warehouse-dropdown"
  />
  {errors.locationCode && <span className="text-xs text-red-500">{errors.locationCode}</span>}
</div>
                  
                  {/* Updated Status dropdown to use IsActive */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Status</label>
                    <select 
                      name="IsActive" 
                      value={header.IsActive} 
                      onChange={handleHeaderChange} 
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                    >
                      {STATUS_OPTIONS.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="flex flex-col gap-2 md:col-span-2">
                    <label className="block text-sm font-medium text-[#64748b]">Remarks</label>
                    <textarea 
                      name="remarks" 
                      value={header.remarks} 
                      onChange={handleHeaderChange} 
                      rows={3}
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                    />
                  </div>
                </div>
              </div>
              
              {/* SO Items */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#384959]">Sales Order Items</h3>
                  <div className="flex gap-2">
                    {savedItems.length > 0 && (
                      <button 
                        type="button" 
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm flex items-center"
                        onClick={loadSavedItemsFromTable}
                        title="Load saved items from table"
                      >
                        <SaveIcon className="mr-1" size={12} /> Reload Saved
                      </button>
                    )}
                    <button 
                      type="button" 
                      className="px-4 py-2 bg-[#6A89A7] text-white rounded-lg hover:bg-[#88BDF2] flex items-center transition-colors" 
                      onClick={addItem}
                    >
                      <PlusIcon className="mr-1" /> Add Item
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto custom-scrollbar">
                  <table className="min-w-full border border-[#E2E8F0]">
                    <thead>
                      <tr className="bg-[#BDDDFC] text-[#384959]">
                        <th className="p-2">#</th>
                        <th className="p-2">Product</th>
                        <th className="p-2">Product Code</th>
                        <th className="p-2">Description</th>
                        <th className="p-2">UOM</th>
                        <th className="p-2">Qty</th>
                        <th className="p-2">Unit Price</th>
                        <th className="p-2">Discount</th>
                        <th className="p-2">Tax</th>
                        <th className="p-2">Tax Rate</th>
                        <th className="p-2">Tax Amount</th>
                        <th className="p-2">Total</th>
                        {/* Department column - Only shown if ShowOnItem is true */}
                        {showDepartmentItemField && <th className="p-2">Department</th>}
                        <th className="p-2">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => {
                        // Find the selected tax code to display its rate
                        const selectedTax = dropdownData.taxCodes.find(t => t.TaxCode === item.taxCode);
                        const taxRate = selectedTax && selectedTax.TaxPercentage ? parseFloat(selectedTax.TaxPercentage) : 0;
                        
                        // Find the selected UOM to display its name
                        const selectedUOM = dropdownData.products.find(p => p.UOMID === parseInt(item.uomID));
                        const uomName = selectedUOM && selectedUOM.UOMName ? selectedUOM.UOMName : '';
                        
                        return (
                          <tr key={idx} className="border-t border-[#E2E8F0]">
                            <td className="p-2 text-center">{idx + 1}</td>
                            <td className="p-2">
  <SearchableDropdown
    options={dropdownData.products}
    value={item.productCode}
    onChange={(productCode) => handleProductChange(idx, productCode)}
    onMultiSelectTrigger={() => setShowMultiSelectModal(true)}
    multiSelectShortcut="p"
    getOptionLabel={(product) => product.ProductName}
    getOptionValue={(product) => product.ProductCode}
    getOptionSecondaryLabel={(product) => product.ProductCode}
    placeholder="Select Product"
    width="w-40"
    error={errors[`productName_${idx}`]}
    className="data-product-dropdown"
  />
  {errors[`productName_${idx}`] && <span className="text-xs text-red-500">*</span>}
</td>
                            <td className="p-2">
                              <div className="flex items-center">
                                <input 
                                  name="productCodeDisplay" 
                                  value={item.productCode} 
                                  readOnly 
                                  className="w-24 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] uppercase"
                                  title="Auto-populated from product selection"
                                />
                                {item.savedToTable && (
                                  <span className="saved-item-badge">Saved</span>
                                )}
                              </div>
                            </td>
                            <td className="p-2">
                              <input 
                                name="description" 
                                value={item.description} 
                                onChange={e => handleItemChange(idx, e)} 
                                className={`w-40 border rounded-lg px-2 py-1 ${errors[`description_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase`}
                              />
                              {errors[`description_${idx}`] && <span className="text-xs text-red-500">*</span>}
                            </td>
                            <td className="p-2">
                              <input 
                                name="uomID" 
                                value={uomName} 
                                readOnly 
                                className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] uppercase"
                                title="Auto-populated from product selection"
                              />
                              {errors[`uomID_${idx}`] && <span className="text-xs text-red-500">*</span>}
                            </td>
                            <td className="p-2">
                              <input 
                                name="quantity" 
                                type="number" 
                                min="0" 
                                value={item.quantity} 
                                onChange={e => handleItemChange(idx, e)} 
                                className={`w-16 border rounded-lg px-2 py-1 ${errors[`quantity_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase`}
                              />
                              {errors[`quantity_${idx}`] && <span className="text-xs text-red-500">*</span>}
                            </td>
                            <td className="p-2">
                              <input 
                                name="unitPrice" 
                                type="number" 
                                min="0" 
                                step="0.01"
                                value={item.unitPrice} 
                                onChange={e => handleItemChange(idx, e)} 
                                className={`w-20 border rounded-lg px-2 py-1 ${errors[`unitPrice_${idx}`] ? 'border-red-400' : 'border-[#E2E8F0]'} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase`}
                              />
                              {errors[`unitPrice_${idx}`] && <span className="text-xs text-red-500">*</span>}
                            </td>
                            <td className="p-2">
                              <input 
                                name="discount" 
                                type="number" 
                                min="0" 
                                step="0.01"
                                value={item.discount} 
                                onChange={e => handleItemChange(idx, e)} 
                                className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase"
                              />
                            </td>
                            <td className="p-2">
  <SearchableDropdown
    options={dropdownData.taxCodes}
    value={item.taxCode}
     onChange={(taxCode) => {
    console.log('Tax code selected:', taxCode);
    // Create a synthetic event to match handleItemChange format
    handleItemChange(idx, { target: { name: 'taxCode', value: taxCode } });
  }}
    getOptionLabel={(tax) => `${tax.TaxName} (${tax.TaxCode})`}
    getOptionValue={(tax) => tax.TaxCode}
    getOptionSecondaryLabel={(tax) => `${tax.Rate
}%`}
    placeholder="Select Tax"
    width="w-30"
    error={errors[`taxCode_${idx}`]}
     className="data-tax-dropdown"
  />
  {errors[`taxCode_${idx}`] && <span className="text-xs text-red-500">*</span>}
</td>
                          <td className="p-2">
  <div className="flex items-center">
    <span className="text-sm">{item.taxRate || '0'}%</span>
  </div>
</td>
<td className="p-2">
  <input 
    name="taxAmount" 
    value={item.taxAmount || '0'} 
    readOnly 
    className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]"
  />
</td>
                            <td className="p-2">
                              <input 
                                name="total" 
                                value={item.total} 
                                readOnly 
                                className="w-20 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]"
                              />
                            </td>
                            {/* Department cell - Only shown if ShowOnItem is true */}
                            {showDepartmentItemField && (
                              <td className="p-2">
                                <select
                                  name="department_id"
                                  value={item.department_id}
                                  onChange={(e) => handleItemChange(idx, e)}
                                  className={`w-24 border rounded-lg px-2 py-1 ${
                                    errors[`department_id_${idx}`]
                                      ? "border-red-400"
                                      : "border-[#E2E8F0]"
                                  } focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase`}
                                >
                                  <option value="">Select</option>
                                  {departments.map((dept) => (
                                    <option key={dept.DepartmentID} value={dept.DepartmentID}>
                                      {dept.DepartmentCode}
                                    </option>
                                  ))}
                                </select>
                                {errors[`department_id_${idx}`] && (
                                  <span className="text-xs text-red-500">*</span>
                                )}
                              </td>
                            )}
                            <td className="p-2 text-center">
                              <button 
                                type="button" 
                                className="text-red-500 hover:text-red-700 transition-colors" 
                                onClick={() => removeItem(idx)} 
                                disabled={items.length === 1}
                              >
                                <TrashIcon />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                {/* Total Summary Section */}
                <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                  <h4 className="text-md font-semibold text-[#384959] mb-4">Total Summary</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Subtotal</span>
                      <span className="text-base font-semibold">{summary.subtotal}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Discount</span>
                      <span className="text-base font-semibold">{summary.totalDiscount}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Tax</span>
                      <span className="text-base font-semibold">{summary.totalTax}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Grand Total</span>
                      <span className="text-base font-semibold text-lg text-blue-600">{summary.grandTotal}</span>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
          
          {/* Fixed Footer with Buttons */}
          <div className="flex-shrink-0 p-8 pt-0">
            <div className="flex justify-end gap-3">
              <button 
                type="button" 
                className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" 
                onClick={onClose}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 bg-[#6A89A7] hover:bg-[#88BDF2] disabled:opacity-50 disabled:cursor-not-allowed" 
                disabled={loading || isSubmitting || !isUserValid}
                onClick={handleSubmit}
              >
                {loading || isSubmitting ? 'Saving...' : (isEdit ? 'Update SO' : 'Save SO')}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <ProductMultiSelectModal
        open={showMultiSelectModal}
        onClose={() => setShowMultiSelectModal(false)}
        products={dropdownData.products}
        onConfirm={handleMultiSelectProducts}
        onSaveToTable={savePopupItemsToTable}
        onAutofill={autofillSelectedProducts}
      />
      
     
    </>
  );
};

export default SalesOrderFormModal;